export class MarqueeElement extends HTMLElement {
  private animationId: number | null = null
  private scrollPosition: number = 0
  private content: HTMLElement | null = null
  private autoplaySpeed: number = 3000
  private isPaused: boolean = false
  private contentWidth: number = 0
  private resizeObserver: ResizeObserver | null = null
  private originalChildren: Node[] = []

  connectedCallback() {
    this.setupMarquee()
    this.startAnimation()
  }

  disconnectedCallback() {
    this.stopAnimation()
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  private setupMarquee() {
    const speedAttr = this.getAttribute('data-autoplay-speed')
    if (speedAttr) {
      this.autoplaySpeed = parseInt(speedAttr, 10) || 3000
    }

    this.originalChildren = Array.from(this.childNodes)

    this.content = document.createElement('div')
    this.content.style.cssText = `
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      will-change: transform;
      min-width: max-content;
    `

    this.originalChildren.forEach(child => {
      this.content!.appendChild(child)
    })

    this.originalChildren.forEach(child => {
      const clone = child.cloneNode(true)
      this.content!.appendChild(clone)
    })

    this.appendChild(this.content)

    this.setupResizeObserver()

    setTimeout(() => this.calculateContentWidth(), 0)

    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('mouseenter', () => this.pause())
      this.addEventListener('mouseleave', () => this.resume())
    }
  }

  private setupResizeObserver() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateContentWidth()
      })

      if (this.content) {
        this.resizeObserver.observe(this.content)
      }
    }
  }

  private calculateContentWidth() {
    if (this.content && this.originalChildren.length > 0) {
      this.contentWidth = this.content.scrollWidth / 2
    }
  }

  private startAnimation() {
    if (this.animationId) return

    const animate = () => {
      if (this.isPaused) {
        this.animationId = requestAnimationFrame(animate)
        return
      }

      if (this.content && this.contentWidth > 0) {
        const speedMultiplier = Math.max(0.2, 3000 / this.autoplaySpeed)
        this.scrollPosition += speedMultiplier
        this.content.style.transform = `translateX(-${this.scrollPosition}px)`

        if (this.scrollPosition >= this.contentWidth) {
          this.scrollPosition = 0
        }
      }

      this.animationId = requestAnimationFrame(animate)
    }

    this.animationId = requestAnimationFrame(animate)
  }

  private stopAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  private pause() {
    this.isPaused = true
  }

  private resume() {
    this.isPaused = false
  }

  public pauseMarquee() {
    this.pause()
  }

  public resumeMarquee() {
    this.resume()
  }

  public setSpeed(speed: number) {
    this.autoplaySpeed = speed
  }

  public recalculateWidth() {
    this.calculateContentWidth()
  }
}
